import React from "react";
import { useNavigate } from "react-router-dom";

import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import Switch from "@/sot/primitives/Switch.jsx";
import SettingsToggleRow from "@/sot/primitives/SettingsToggleRow.jsx";
import Button from "@/components/ui/Button.jsx";
import Card from "@/components/ui/Card.jsx";
import { BasicDialogFrame } from "@/components/ui/BasicDialog.jsx";
import BasicDialogCritical from "@/components/ui/BasicDialogCritical.jsx";
import supabase from "@/services/supabaseClient";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import {
  DEFAULT_MESSAGE_ALERT_SOUND,
  buildAppointmentReminderPatch,
  buildDeviceAlertsPatch,
  getBrowserNotificationPermission,
  mapDbPrefsToUi,
  readNotificationPrefsCache,
  writeNotificationPrefsCache,
} from "@/utils/notificationPreferences.js";
import { patchOwnNotificationPreferences } from "@/services/notificationPreferencesService.js";
import {
  getFirebasePushConfig,
  getFirebasePushRuntimeDiagnostics,
  readServiceWorkerPushDiagnostics,
  registerFirebasePushDevice,
  sendFirebaseBackgroundDiagnostic,
  sendFirebasePushTest,
  testDirectBrowserNotification,
  testServiceWorkerNotification,
} from "@/services/firebasePushService.js";
import { runMessageAlertPreview } from "@/utils/deviceAlerts.js";
import { canVibrate } from "@/utils/haptics.js";

const CAPABILITIES = {
  appointmentReminderScheduler: true,
  emailReminderDelivery: true,
  smsReminderDelivery: false,
  backgroundPushDelivery: true,
  urgentAlertSound: true,
};
const PUSH_DEBUG_PREFIX = "[AfriMed Push]";

function pushDebug(label, details = {}) {
  if (typeof console === "undefined") return;
  try {
    console.info(PUSH_DEBUG_PREFIX, label, details);
  } catch {
    // Temporary proof logging must never affect the settings flow.
  }
}

function toBool(next) {
  if (typeof next === "boolean") return next;
  if (next && typeof next === "object" && "target" in next) {
    return Boolean(next.target?.checked);
  }
  return Boolean(next);
}

function isMissingPrefsColumn(error) {
  const msg = String(error?.message || "").toLowerCase();
  return msg.includes("notification_prefs") && msg.includes("column");
}

function samePrefs(left = {}, right = {}) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function pushRegistrationSucceeded(result) {
  return Boolean(
    (result?.registered === true || result?.ok === true) &&
      (result?.device?.id || result?.deviceId || result?.device?.token)
  );
}

function getRegisteredDeviceId(result) {
  return String(result?.device?.id || result?.deviceId || "").trim();
}

function maskIdentifier(value = "") {
  const clean = String(value || "").trim();
  if (!clean) return "";
  if (clean.length <= 10) return clean;
  return `${clean.slice(0, 4)}…${clean.slice(-6)}`;
}

function summarizeSendResult(result = {}) {
  return {
    pushSent: Boolean(result?.pushSent),
    fallbackInApp: Boolean(result?.fallbackInApp || result?.inAppProof),
    deviceId: String(result?.deviceId || result?.targetDeviceId || "").trim(),
    firebaseResponseName: result?.firebaseResponse?.name || null,
    reason: result?.reason || null,
    payloadType: result?.payloadMode || "notification_plus_data",
    delaySeconds: Number(result?.delaySeconds || 0),
  };
}

function mergePushEvents(currentEvents = [], incomingEvents = []) {
  const seen = new Set();
  return [...incomingEvents, ...currentEvents]
    .filter((event) => {
      const key = `${event?.at || ""}:${event?.phase || ""}:${event?.type || ""}:${event?.detail || ""}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 12);
}

function hasPushEvent(events = [], phase) {
  return events.some((event) => event?.phase === phase);
}

function findPushEvent(events = [], phase) {
  return events.find((event) => event?.phase === phase);
}

function isStagingPushDiagnosticsEnabled() {
  if (typeof window === "undefined") return false;
  const host = String(window.location.hostname || "").toLowerCase();
  const isStagingHost =
    host === "staging.afrimed.tech" ||
    host.endsWith("--afrimed-staging.netlify.app") ||
    host === "localhost" ||
    host === "127.0.0.1";
  if (!isStagingHost) return false;
  return new URLSearchParams(window.location.search).get("debugPush") === "1";
}

function normalizeForStagingCapabilities(next = {}, vibrationSupported = false) {
  return {
    ...next,
    appointmentRemindersOn: CAPABILITIES.appointmentReminderScheduler
      ? Boolean(next.appointmentRemindersOn)
      : false,
    inAppOn: Boolean(next.inAppOn),
    emailOn:
      CAPABILITIES.emailReminderDelivery && Boolean(next.emailOn),
    smsOn:
      CAPABILITIES.appointmentReminderScheduler &&
      CAPABILITIES.smsReminderDelivery &&
      Boolean(next.smsOn),
    soundOn: CAPABILITIES.urgentAlertSound && Boolean(next.soundOn),
    browserPushOn:
      CAPABILITIES.backgroundPushDelivery && Boolean(next.browserPushOn),
    vibrationOn: vibrationSupported && Boolean(next.vibrationOn),
  };
}

function StatusBadge({ children }) {
  return (
    <span className="ml-2 inline-flex shrink-0 items-center rounded-full bg-[rgba(24,50,119,0.08)] px-2 py-0.5 text-label-small text-on-surface-var">
      {children}
    </span>
  );
}

function ComingSoonBadge({ t }) {
  const { languageCode } = useLanguage();
  const isFrench = isFrenchLanguageCode(languageCode);
  return <StatusBadge>{t("common.comingSoon", isFrench ? "Bientôt" : "Coming soon")}</StatusBadge>;
}

function rowTitle(label, badge, t) {
  if (!badge) return label;
  return (
    <span className="inline-flex min-w-0 items-center">
      <span className="truncate">{label}</span>
      <ComingSoonBadge t={t} />
    </span>
  );
}

function isFrenchLanguageCode(value = "") {
  return String(value || "").toLowerCase().startsWith("fr");
}

function getBrowserPushCapabilitySnapshot() {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return {
      hasWindow: false,
      hasNotificationApi: false,
      hasServiceWorkerApi: false,
      hasPushManagerApi: false,
      supported: false,
      definitelyUnsupported: true,
    };
  }

  const hasNotificationApi = "Notification" in window;
  const hasServiceWorkerApi = "serviceWorker" in navigator;
  const hasPushManagerApi = "PushManager" in window;
  const supported = hasNotificationApi && hasServiceWorkerApi && hasPushManagerApi;

  return {
    hasWindow: true,
    hasNotificationApi,
    hasServiceWorkerApi,
    hasPushManagerApi,
    supported,
    definitelyUnsupported: !hasNotificationApi && !hasServiceWorkerApi && !hasPushManagerApi,
  };
}

function getPushRegistrationFailureReason(reason = "") {
  const normalized = String(reason || "").trim();
  if (normalized === "firebase_token_unregistered" || normalized === "missing_fcm_token") {
    return "stale_or_missing_token";
  }
  if (
    normalized === "browser_notifications_unavailable" ||
    normalized === "firebase_messaging_unsupported" ||
    normalized === "service_worker_registration_failed" ||
    normalized === "service_worker_unavailable"
  ) {
    return "not_active";
  }
  return normalized || "";
}

export default function NotificationPreferencesCommon({ role = "patient" }) {
  const navigate = useNavigate();
  const auth = (typeof useAuth === "function" ? useAuth() : {}) || {};
  const { t, languageCode } = useLanguage();
  const isFrench = isFrenchLanguageCode(languageCode);
  const userId = String(auth?.user?.id || "").trim();
  const basePath = `/settings/${role === "doctor" ? "doctor" : "patient"}`;
  const profileTable = role === "doctor" ? "doctor_profiles" : "patient_profiles";
  const [prefs, setPrefs] = React.useState(() =>
    mapDbPrefsToUi(readNotificationPrefsCache(role, userId), role)
  );
  const prefsRef = React.useRef(prefs);
  const [prefsRevision, setPrefsRevision] = React.useState(null);
  const [hydrated, setHydrated] = React.useState(false);
  const [confirmReminderDeactivation, setConfirmReminderDeactivation] = React.useState(false);
  const [pushTestState, setPushTestState] = React.useState({
    busy: false,
    message: "",
  });
  const [pushVisibleProof, setPushVisibleProof] = React.useState(null);
  const [pushDiagnostics, setPushDiagnostics] = React.useState({
    busy: "",
    runtime: null,
    browserNotification: null,
    serviceWorkerNotification: null,
    firebaseSend: null,
    firebaseBackgroundSend: null,
    payloadSends: {},
    serviceWorkerDiagnostics: null,
    pushEvents: [],
    message: "",
  });
  const [browserPermission, setBrowserPermission] = React.useState(() =>
    getBrowserNotificationPermission()
  );
  const [pushSessionStatus, setPushSessionStatus] = React.useState("");
  const showPushDiagnostics = React.useMemo(
    () => isStagingPushDiagnosticsEnabled(),
    []
  );
  const firebasePushConfig = React.useMemo(() => getFirebasePushConfig(), []);
  const firebasePushConfigured = firebasePushConfig.missing.length === 0;
  const browserPushCapability = React.useMemo(() => getBrowserPushCapabilitySnapshot(), []);
  const browserPushSupported =
    browserPushCapability.supported ||
    browserPermission === "granted" ||
    Boolean(prefs.browserPushOn);
  const browserPushDefinitelyUnsupported =
    !browserPushSupported && browserPushCapability.definitelyUnsupported;
  const browserPushDenied = browserPermission === "denied";
  const browserPushGranted = browserPermission === "granted";
  const vibrationSupported = canVibrate();
  const currentPushRegistered =
    CAPABILITIES.backgroundPushDelivery &&
    firebasePushConfigured &&
    browserPushGranted &&
    prefs.browserPushOn;
  const pushNeedsRefresh = pushSessionStatus === "stale_or_missing_token";
  const canAttemptPushRegistration =
    CAPABILITIES.backgroundPushDelivery &&
    firebasePushConfigured &&
    !browserPushDenied &&
    !browserPushDefinitelyUnsupported;

  React.useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const {
          data: { user },
          error: authErr,
        } = await supabase.auth.getUser();

        if (authErr || !user || !alive) {
          if (alive) setHydrated(true);
          return;
        }

        const { data, error } = await supabase
          .from(profileTable)
          .select("notification_prefs, updated_at")
          .eq("user_id", user.id)
          .maybeSingle();

        if (error) {
          if (!isMissingPrefsColumn(error)) {
            console.error("Notification preferences hydrate failed", error);
          }
          if (alive) setHydrated(true);
          return;
        }

        if (!alive) return;

        const fromDb = normalizeForStagingCapabilities(
          mapDbPrefsToUi(data?.notification_prefs, role),
          vibrationSupported
        );
        prefsRef.current = fromDb;
        setPrefs(fromDb);
        setPrefsRevision(data?.updated_at || null);
        writeNotificationPrefsCache(role, user.id, fromDb);
      } finally {
        if (alive) setHydrated(true);
      }
    })();

    return () => {
      alive = false;
    };
  }, [profileTable, role, vibrationSupported]);

  React.useEffect(() => {
    const syncPermission = () => setBrowserPermission(getBrowserNotificationPermission());
    syncPermission();
    window.addEventListener("focus", syncPermission);
    document.addEventListener("visibilitychange", syncPermission);
    return () => {
      window.removeEventListener("focus", syncPermission);
      document.removeEventListener("visibilitychange", syncPermission);
    };
  }, []);

  React.useEffect(() => {
    const next = normalizeForStagingCapabilities(prefs, vibrationSupported);
    prefsRef.current = next;
    writeNotificationPrefsCache(role, userId, next);
  }, [prefs, role, userId, vibrationSupported]);

  const applyPreferenceIntent = React.useCallback(
    async (nextPrefs, patch, source) => {
      const normalized = normalizeForStagingCapabilities(nextPrefs, vibrationSupported);
      prefsRef.current = normalized;
      setPrefs(normalized);
      writeNotificationPrefsCache(role, userId, normalized);

      const { data, error } = await patchOwnNotificationPreferences({
        role,
        expectedUpdatedAt: prefsRevision,
        patch,
        source,
      });

      if (error) {
        console.error("Notification preference intent save failed", error);
        // A stale optimistic revision must never retry with a whole document.
        // The next visible hydration/read becomes the source for a fresh intent.
        return false;
      }

      if (data?.notification_prefs) {
        const persisted = normalizeForStagingCapabilities(
          mapDbPrefsToUi(data.notification_prefs, role),
          vibrationSupported,
        );
        prefsRef.current = persisted;
        setPrefs(persisted);
        setPrefsRevision(data.updated_at || null);
        writeNotificationPrefsCache(role, userId, persisted);
      }
      return true;
    },
    [prefsRevision, role, userId, vibrationSupported],
  );

  React.useEffect(() => {
    setPrefs((current) => {
      const next = normalizeForStagingCapabilities(current, vibrationSupported);
      prefsRef.current = next;
      return samePrefs(current, next) ? current : next;
    });
  }, [vibrationSupported]);

  React.useEffect(() => {
    if (!showPushDiagnostics) return;
    refreshPushDiagnostics();
  }, [showPushDiagnostics]);

  React.useEffect(() => {
    if (!showPushDiagnostics || typeof window === "undefined") return undefined;

    function appendPushDiagnosticEvent(detail = {}) {
      setPushDiagnostics((current) => ({
        ...current,
        pushEvents: mergePushEvents(current.pushEvents || [], [
          {
            at: detail?.at || new Date().toISOString(),
            phase: detail?.phase || "unknown",
            type: detail?.type || null,
            source: detail?.source || null,
            hasNotification: Boolean(detail?.hasNotification),
            hasData: Boolean(detail?.hasData),
            payloadMode: detail?.payloadMode || null,
            detail: detail?.detail || detail?.reason || null,
          },
        ]),
      }));
    }

    function handleWindowDiagnostic(event) {
      appendPushDiagnosticEvent(event?.detail || {});
    }

    function handleServiceWorkerDiagnostic(event) {
      if (event?.data?.type !== "AFRIMED_PUSH_DIAGNOSTIC") return;
      appendPushDiagnosticEvent(event.data.detail || {});
    }

    window.addEventListener("afrimed:push-diagnostic", handleWindowDiagnostic);
    navigator.serviceWorker?.addEventListener?.("message", handleServiceWorkerDiagnostic);

    return () => {
      window.removeEventListener("afrimed:push-diagnostic", handleWindowDiagnostic);
      navigator.serviceWorker?.removeEventListener?.("message", handleServiceWorkerDiagnostic);
    };
  }, [showPushDiagnostics]);

  const titleCopy = t(
    "settings.notificationPreferences.appointmentReminders.title",
    isFrench ? "Planification des rappels de rendez-vous" : "Appointment reminder scheduling",
  );
  const introCopy = t(
    "settings.notificationPreferences.intro",
    isFrench
      ? "Choisissez comment AfriMed™ vous alerte pour les rappels de rendez-vous et les mises à jour de soins urgentes."
      : "Choose how AfriMed™ alerts you for appointment reminders and urgent care updates.",
  );
  const timingCopy = t(
    "settings.notificationPreferences.appointmentReminders.schedulerActive",
    isFrench ? "Recevoir des rappels automatiques avant les prochains rendez-vous." : "Receive automatic reminders before upcoming appointments.",
  );

  async function handlePushToggle(next) {
    setBrowserPermission(getBrowserNotificationPermission());
    if (!firebasePushConfigured) {
      setPushSessionStatus("missing_config");
      setPushTestState({
        busy: false,
        message: t(
          "settings.notificationPreferences.channels.push.missingConfig",
          isFrench
            ? "Firebase Push n’est pas configuré pour cet environnement de staging. Les rappels dans AfriMed™ restent actifs."
            : "Firebase push is not configured for this staging environment yet. In-app reminders remain active.",
        ),
      });
      return;
    }
    if (browserPushDenied) {
      setPushSessionStatus("permission_denied");
      return;
    }
    if (browserPushDefinitelyUnsupported || !CAPABILITIES.backgroundPushDelivery) return;
    const on = toBool(next);
    pushDebug("push toggle requested", {
      enabled: on,
      browserPushSupported,
      browserPermission,
      role,
    });
    if (!on) {
      const nextPrefs = { ...prefsRef.current, browserPushOn: false };
      await applyPreferenceIntent(
        nextPrefs,
        {
          appointment_reminders: buildAppointmentReminderPatch(nextPrefs, role),
          device_alerts: buildDeviceAlertsPatch(nextPrefs, role),
        },
        "notification_settings",
      );
      return;
    }

    setPushTestState({
      busy: true,
      message: t(
        "settings.notificationPreferences.channels.push.registering",
        isFrench ? "Préparation des notifications navigateur pour AfriMed™." : "Preparing browser push for AfriMed™.",
      ),
    });

    try {
      const result = await registerFirebasePushDevice();
      setBrowserPermission(getBrowserNotificationPermission());
      const ok = pushRegistrationSucceeded(result);
      const failureReason = getPushRegistrationFailureReason(result?.reason);
      pushDebug("push registration response", {
        ok,
        registered: Boolean(result?.registered),
        hasDeviceId: Boolean(result?.device?.id || result?.deviceId),
        reason: result?.reason || null,
        provider: result?.provider || null,
        platform: result?.platform || null,
      });
      const nextPrefs = { ...prefsRef.current, browserPushOn: ok };
      await applyPreferenceIntent(
        nextPrefs,
        {
          appointment_reminders: buildAppointmentReminderPatch(nextPrefs, role),
          device_alerts: buildDeviceAlertsPatch(nextPrefs, role),
        },
        "notification_settings",
      );
      setPushSessionStatus(ok ? "registered" : failureReason || "not_active");
      setPushTestState({
        busy: false,
        message: ok
          ? t(
              "settings.notificationPreferences.channels.push.registered",
              isFrench ? "Les notifications navigateur sont prêtes pour les tests." : "Browser push is ready for test notifications.",
            )
          : result?.reason === "denied"
            ? t(
                "settings.notificationPreferences.channels.push.denied",
                isFrench
                  ? "Les notifications push sont bloquées dans les paramètres du navigateur. Activez les notifications pour AfriMed™ puis réessayez."
                  : "Push notifications are blocked in your browser settings. Enable notifications for AfriMed™ and try again.",
              )
            : failureReason === "stale_or_missing_token"
              ? t(
                  "settings.notificationPreferences.channels.push.refreshNeeded",
                  isFrench
                    ? "Les notifications push doivent être actualisées pour ce navigateur."
                    : "Push notifications need to be refreshed for this browser.",
                )
            : browserPushDefinitelyUnsupported
              ? t(
                  "settings.notificationPreferences.channels.push.unsupportedDetailed",
                  isFrench
                    ? "Les notifications push ne sont pas prises en charge par ce navigateur. Vous recevrez toujours les rappels dans AfriMed™ lorsque vous êtes connecté."
                    : "Push notifications are not supported on this browser. You will still receive in-app reminders while signed in.",
                )
          : t(
              "settings.notificationPreferences.channels.push.notReady",
              isFrench
                ? "Les notifications navigateur n’ont pas encore pu être enregistrées. Les notifications dans AfriMed™ restent actives."
                : "Browser push could not be registered yet. In-app notifications remain active.",
            ),
      });
    } catch (error) {
      pushDebug("push registration failure", {
        message: error?.message || String(error || "unknown_error"),
      });
      const nextPrefs = { ...prefsRef.current, browserPushOn: false };
      await applyPreferenceIntent(
        nextPrefs,
        {
          appointment_reminders: buildAppointmentReminderPatch(nextPrefs, role),
          device_alerts: buildDeviceAlertsPatch(nextPrefs, role),
        },
        "notification_settings",
      );
      setPushSessionStatus("not_active");
      setPushTestState({
        busy: false,
        message:
          error?.message ||
          t(
            "settings.notificationPreferences.channels.push.failed",
            isFrench ? "Les notifications navigateur n’ont pas encore pu être enregistrées." : "Browser push could not be registered yet.",
          ),
      });
    }
  }

  async function handlePushTest() {
    setBrowserPermission(getBrowserNotificationPermission());
    if (!firebasePushConfigured) {
      setPushSessionStatus("missing_config");
      setPushTestState({
        busy: false,
        message: t(
          "settings.notificationPreferences.channels.push.missingConfig",
          isFrench
            ? "Firebase Push n’est pas configuré pour cet environnement de staging. Les rappels dans AfriMed™ restent actifs."
            : "Firebase push is not configured for this staging environment yet. In-app reminders remain active.",
        ),
      });
      return;
    }
    if (browserPushDenied) {
      setPushSessionStatus("permission_denied");
      setPushTestState({
        busy: false,
        message: t(
          "settings.notificationPreferences.channels.push.denied",
          isFrench
            ? "Les notifications push sont bloquées dans les paramètres du navigateur. Activez les notifications pour AfriMed™ puis réessayez."
            : "Push notifications are blocked in your browser settings. Enable notifications for AfriMed™ and try again.",
        ),
      });
      return;
    }
    pushDebug("push-test ui click", {
      role,
      browserPermission,
      browserPushSupported,
    });
    setPushVisibleProof(null);
    setPushTestState({
      busy: true,
      message: t(
        "settings.notificationPreferences.channels.push.testing",
        isFrench ? "Envoi d’un test de notification AfriMed™." : "Sending AfriMed™ push test.",
      ),
    });

    try {
      pushDebug("push-test registration refresh attempted", { phase: "before_send" });
      const registrationResult = await registerFirebasePushDevice();
      setBrowserPermission(getBrowserNotificationPermission());
      const registrationOk = pushRegistrationSucceeded(registrationResult);
      const registrationFailureReason = getPushRegistrationFailureReason(registrationResult?.reason);
      const registeredDeviceId = getRegisteredDeviceId(registrationResult);
      pushDebug("push-test registration refresh result", {
        registered: Boolean(registrationResult?.registered),
        ok: registrationOk,
        hasDeviceId: Boolean(registeredDeviceId),
        reason: registrationResult?.reason || null,
      });

      if (!registrationOk || !registeredDeviceId) {
        setPrefs((current) => ({ ...current, browserPushOn: false }));
        setPushSessionStatus(registrationFailureReason || "not_active");
        setPushVisibleProof({
          tone: "warning",
          title: t(
            "settings.notificationPreferences.channels.push.notRegisteredTitle",
            isFrench ? "Navigateur non enregistré" : "Browser not registered",
          ),
          body:
            registrationResult?.reason === "denied"
              ? t(
                  "settings.notificationPreferences.channels.push.chromeGuidance",
                  isFrench
                    ? "Activez les notifications pour staging.afrimed.tech dans Chrome, puis vérifiez les réglages de notification de l’ordinateur et le mode Ne pas déranger."
                    : "In Chrome, open the lock icon next to staging.afrimed.tech and make sure Notifications are allowed. Also check your computer notification settings and Focus/Do Not Disturb mode.",
                )
              : t(
                  "settings.notificationPreferences.channels.push.registrationRequiredBody",
                  isFrench
                    ? "Activez les notifications push puis réessayez. Les notifications dans AfriMed™ restent actives."
                    : "Please enable push notifications and try again. In-app notifications remain active.",
                ),
        });
        setPushTestState({
          busy: false,
          message:
            registrationResult?.reason === "denied"
              ? t(
                  "settings.notificationPreferences.channels.push.denied",
                  isFrench
                    ? "Les notifications push sont bloquées dans les paramètres du navigateur. Activez les notifications pour AfriMed™ puis réessayez."
                    : "Push notifications are blocked in your browser settings. Enable notifications for AfriMed™ and try again.",
                )
              : registrationFailureReason === "stale_or_missing_token"
              ? t(
                  "settings.notificationPreferences.channels.push.refreshNeeded",
                  isFrench
                    ? "Les notifications push doivent être actualisées pour ce navigateur."
                    : "Push notifications need to be refreshed for this browser.",
                )
              : t(
                  "settings.notificationPreferences.channels.push.registrationRequired",
                  isFrench
                    ? "Les notifications push ne sont pas actives dans cette session de navigateur."
                    : "Push notifications are not active in this browser session.",
                ),
        });
        return;
      }

      setPushSessionStatus("registered");

      let retryAttempted = false;
      let result = await sendFirebasePushTest({ role, deviceId: registeredDeviceId });
      if (showPushDiagnostics) {
        setPushDiagnostics((current) => ({
          ...current,
          firebaseSend: {
            ...summarizeSendResult(result),
            retryAttempted,
          },
        }));
      }
      pushDebug("push-test ui response", {
        pushSent: Boolean(result?.pushSent),
        fallbackInApp: Boolean(result?.fallbackInApp),
        deviceId: result?.deviceId || null,
        firebaseResponseName: result?.firebaseResponse?.name || null,
        reason: result?.reason || null,
        retryAttempted,
      });

      if (!result?.pushSent && result?.reason === "firebase_token_unregistered") {
        retryAttempted = true;
        pushDebug("push-test retry registration refresh attempted", {
          reason: result.reason,
        });
        const retryRegistrationResult = await registerFirebasePushDevice({ forceRefresh: true });
        const retryRegistrationOk = pushRegistrationSucceeded(retryRegistrationResult);
        const retryDeviceId = getRegisteredDeviceId(retryRegistrationResult);
        pushDebug("push-test retry registration refresh result", {
          registered: Boolean(retryRegistrationResult?.registered),
          ok: retryRegistrationOk,
          hasDeviceId: Boolean(retryDeviceId),
          reason: retryRegistrationResult?.reason || null,
        });
        if (!retryRegistrationOk || !retryDeviceId) {
          setPrefs((current) => ({ ...current, browserPushOn: false }));
          setPushSessionStatus("stale_or_missing_token");
          setPushVisibleProof({
            tone: "warning",
            title: t(
              "settings.notificationPreferences.channels.push.notRegisteredTitle",
              isFrench ? "Navigateur non enregistré" : "Browser not registered",
            ),
            body: t(
              "settings.notificationPreferences.channels.push.chromeGuidance",
              isFrench
                ? "Activez les notifications pour staging.afrimed.tech dans Chrome, puis vérifiez les réglages de notification de l’ordinateur et le mode Ne pas déranger."
                : "In Chrome, open the lock icon next to staging.afrimed.tech and make sure Notifications are allowed. Also check your computer notification settings and Focus/Do Not Disturb mode.",
            ),
          });
          setPushTestState({
            busy: false,
            message: t(
              "settings.notificationPreferences.channels.push.registrationRefreshFailed",
              isFrench
                ? "Nous n’avons pas pu réenregistrer ce navigateur. Vérifiez les notifications du navigateur puis réessayez."
                : "We could not re-register this browser. Check browser notifications and try again.",
            ),
          });
          return;
        }
        result = await sendFirebasePushTest({ role, deviceId: retryDeviceId });
        setPushSessionStatus("registered");
        if (showPushDiagnostics) {
          setPushDiagnostics((current) => ({
            ...current,
            firebaseSend: {
              ...summarizeSendResult(result),
              retryAttempted,
            },
          }));
        }
        pushDebug("push-test ui response", {
          pushSent: Boolean(result?.pushSent),
          fallbackInApp: Boolean(result?.fallbackInApp),
          deviceId: result?.deviceId || null,
          firebaseResponseName: result?.firebaseResponse?.name || null,
          reason: result?.reason || null,
          retryAttempted,
        });
      }

      if (result?.fallbackInApp || result?.inAppProof) {
        window.dispatchEvent(new Event("afrimed:notifications:changed"));
        const alertPreview = await runMessageAlertPreview({
          soundProfile: prefs.soundOn ? prefs.messageAlertSound : "off",
          vibrationOn: Boolean(prefs.vibrationOn),
        });
        setPushVisibleProof({
          tone: "success",
          title: t(
            "settings.notificationPreferences.channels.push.inAppDeliveredTitle",
            isFrench ? "Notification livrée dans AfriMed™" : "Test notification delivered inside AfriMed™",
          ),
          body: t(
            "settings.notificationPreferences.channels.push.inAppDeliveredBody",
            isFrench
              ? "La livraison push du navigateur peut dépendre des paramètres de notification de l’appareil."
              : "Browser push delivery may depend on your device notification settings.",
          ),
        });
        pushDebug("push-test local alert preview", {
          soundPlayed: Boolean(alertPreview?.soundPlayed),
          vibrationTriggered: Boolean(alertPreview?.vibrationTriggered),
        });
      } else if (result?.pushSent) {
        setPushVisibleProof({
          tone: "info",
          title: t(
            "settings.notificationPreferences.channels.push.deliveryUnconfirmedTitle",
            isFrench ? "Test envoyé à ce navigateur" : "Test sent to this browser",
          ),
          body: t(
            "settings.notificationPreferences.channels.push.deliveryUnconfirmedBody",
            isFrench
              ? "La livraison navigateur ne peut pas être confirmée ici. Si aucune notification système n’apparaît, vérifiez les notifications de l’ordinateur et le mode Ne pas déranger."
              : "Browser delivery cannot be confirmed here. If no system notification appears, check your computer notification settings and Focus/Do Not Disturb mode.",
          ),
        });
      } else {
        setPushVisibleProof({
          tone: "warning",
          title: t(
            "settings.notificationPreferences.channels.push.testNotDeliveredTitle",
            isFrench ? "Test non livré" : "Test not delivered",
          ),
          body: t(
            "settings.notificationPreferences.channels.push.testNotDeliveredBody",
            isFrench
              ? "Nous n’avons pas pu envoyer un test push à ce navigateur. Réenregistrez les notifications push puis réessayez."
              : "We could not send a test push to this browser. Re-register push notifications and try again.",
          ),
        });
      }

      setPushTestState({
        busy: false,
        message: result?.pushSent
          ? result?.fallbackInApp || result?.inAppProof
            ? t(
                "settings.notificationPreferences.channels.push.testFallback",
                isFrench
                  ? "Notification de test livrée dans AfriMed™."
                  : "Test notification delivered inside AfriMed™.",
              )
            : t(
                "settings.notificationPreferences.channels.push.testSent",
                isFrench
                  ? "Test push envoyé à ce navigateur, mais la livraison navigateur ne peut pas être confirmée."
                  : "Test push was sent to this browser, but browser delivery could not be confirmed.",
              )
          : result?.reason === "missing_firebase_project_id" ||
              result?.reason === "missing_firebase_service_account"
            ? t(
                "settings.notificationPreferences.channels.push.serverMissing",
                isFrench
                  ? "L’envoi Firebase n’est pas encore configuré pour cet environnement. La notification interne AfriMed™ a été créée."
                  : "Firebase push sending is not configured for this environment yet. An AfriMed™ in-app notification was created.",
              )
          : !result?.fallbackInApp
            ? t(
                "settings.notificationPreferences.channels.push.testFailed",
                isFrench
                  ? "Nous n’avons pas pu envoyer un test push à ce navigateur. Veuillez réessayer."
                  : "We could not send a test push to this browser. Please try again.",
              )
          : t(
              "settings.notificationPreferences.channels.push.testFallback",
              isFrench
                ? "Notification de test livrée dans AfriMed™. La livraison push du navigateur peut être bloquée par les paramètres de l’appareil."
                : "Test notification delivered inside AfriMed™.",
            ),
      });
    } catch (error) {
      pushDebug("push-test ui failure", {
        message: error?.message || String(error || "unknown_error"),
      });
      setPushTestState({
        busy: false,
        message:
          error?.message ||
          t(
            "settings.notificationPreferences.channels.push.testFailed",
            isFrench ? "Le test de notification AfriMed™ n’a pas pu être envoyé." : "AfriMed™ push test could not be sent.",
          ),
      });
    }
  }

  async function handleFirebaseBackgroundDiagnostic(
    payloadMode = "notification_plus_data",
    delaySeconds = 0
  ) {
    if (!showPushDiagnostics) return;
    setPushDiagnostics((current) => ({
      ...current,
      busy: delaySeconds ? "firebaseBackgroundDelayed" : `firebaseBackground:${payloadMode}`,
      message: t(
        "settings.notificationPreferences.channels.push.firebaseBackgroundDiagnosticRunning",
        delaySeconds
          ? isFrench
            ? "Envoi du diagnostic Firebase différé. Passez AfriMed™ en arrière-plan."
            : "Sending delayed Firebase diagnostic. Background AfriMed™ now."
          : isFrench
            ? "Envoi du diagnostic Firebase en arrière-plan."
            : "Sending Firebase background diagnostic.",
      ),
    }));

    try {
      const registrationResult = await registerFirebasePushDevice();
      setBrowserPermission(getBrowserNotificationPermission());
      const registrationOk = pushRegistrationSucceeded(registrationResult);
      const registeredDeviceId = getRegisteredDeviceId(registrationResult);

      if (!registrationOk || !registeredDeviceId) {
        setPushDiagnostics((current) => ({
          ...current,
          busy: "",
          firebaseBackgroundSend: {
            pushSent: false,
            fallbackInApp: false,
            deviceId: registeredDeviceId,
            reason: registrationResult?.reason || "current_device_not_registered",
            payloadType: payloadMode,
          },
          message: t(
            "settings.notificationPreferences.channels.push.firebaseBackgroundDiagnosticNotRegistered",
            isFrench
              ? "Ce navigateur doit être enregistré avant le test Firebase en arrière-plan."
              : "This browser must be registered before the Firebase background test.",
          ),
        }));
        return;
      }

      const result = await sendFirebaseBackgroundDiagnostic({
        role,
        deviceId: registeredDeviceId,
        payloadMode,
        delaySeconds,
      });
      await new Promise((resolve) => window.setTimeout(resolve, 1500));
      await loadStoredServiceWorkerDiagnostics();
      const summarized = summarizeSendResult(result);
      setPushDiagnostics((current) => ({
        ...current,
        busy: "",
        firebaseBackgroundSend: summarized,
        payloadSends: {
          ...(current.payloadSends || {}),
          [payloadMode]: summarized,
          ...(delaySeconds
            ? { [`${payloadMode}_delayed_${delaySeconds}s`]: summarized }
            : {}),
        },
        message: result?.pushSent
          ? t(
              "settings.notificationPreferences.channels.push.firebaseBackgroundDiagnosticSent",
              delaySeconds
                ? isFrench
                  ? "Diagnostic Firebase différé envoyé au navigateur actuel. Vérifiez si une bannière système apparaît."
                  : "Delayed Firebase diagnostic sent to the current browser. Check whether a system banner appears."
                : isFrench
                  ? "Diagnostic Firebase envoyé au navigateur actuel. Mettez AfriMed™ en arrière-plan et vérifiez la bannière système."
                  : "Firebase diagnostic sent to the current browser. Background AfriMed™ and check for a system banner.",
            )
          : result?.reason || "Firebase background diagnostic failed.",
      }));
    } catch (error) {
      setPushDiagnostics((current) => ({
        ...current,
        busy: "",
        firebaseBackgroundSend: {
          pushSent: false,
          fallbackInApp: false,
          deviceId: "",
          reason: error?.message || String(error || "firebase_background_diagnostic_failed"),
          payloadType: payloadMode,
        },
        message: error?.message || String(error || "Firebase background diagnostic failed."),
      }));
    }
  }

  async function loadStoredServiceWorkerDiagnostics() {
    if (!showPushDiagnostics) return null;
    const result = await readServiceWorkerPushDiagnostics();
    setPushDiagnostics((current) => ({
      ...current,
      serviceWorkerDiagnostics: result,
      pushEvents: mergePushEvents(current.pushEvents || [], result?.events || []),
    }));
    return result;
  }

  async function refreshPushDiagnostics() {
    if (!showPushDiagnostics) return;
    setPushDiagnostics((current) => ({
      ...current,
      busy: "runtime",
      message: t(
        "settings.notificationPreferences.channels.push.diagnosticsLoading",
        isFrench ? "Lecture du diagnostic push." : "Reading push diagnostics.",
      ),
    }));
    try {
      const runtime = await getFirebasePushRuntimeDiagnostics();
      const serviceWorkerDiagnostics = await readServiceWorkerPushDiagnostics();
      setBrowserPermission(getBrowserNotificationPermission());
      setPushDiagnostics((current) => ({
        ...current,
        busy: "",
        runtime,
        serviceWorkerDiagnostics,
        pushEvents: mergePushEvents(
          current.pushEvents || [],
          serviceWorkerDiagnostics?.events || []
        ),
        message: t(
          "settings.notificationPreferences.channels.push.diagnosticsLoaded",
          isFrench ? "Diagnostic push actualisé." : "Push diagnostics refreshed.",
        ),
      }));
    } catch (error) {
      setPushDiagnostics((current) => ({
        ...current,
        busy: "",
        message: error?.message || String(error || "Push diagnostics failed."),
      }));
    }
  }

  async function handleDirectBrowserNotificationDiagnostic() {
    if (!showPushDiagnostics) return;
    setPushDiagnostics((current) => ({
      ...current,
      busy: "browser",
      message: t(
        "settings.notificationPreferences.channels.push.browserDiagnosticRunning",
        isFrench ? "Test de notification navigateur en cours." : "Testing browser notification.",
      ),
    }));
    const result = await testDirectBrowserNotification();
    setBrowserPermission(getBrowserNotificationPermission());
    setPushDiagnostics((current) => ({
      ...current,
      busy: "",
      browserNotification: result,
      message: result?.ok
        ? t(
            "settings.notificationPreferences.channels.push.browserDiagnosticPass",
            isFrench ? "Notification navigateur demandée." : "Browser notification requested.",
          )
        : result?.detail || result?.reason || "Browser notification failed.",
    }));
  }

  async function handleServiceWorkerNotificationDiagnostic() {
    if (!showPushDiagnostics) return;
    setPushDiagnostics((current) => ({
      ...current,
      busy: "serviceWorker",
      message: t(
        "settings.notificationPreferences.channels.push.swDiagnosticRunning",
        isFrench ? "Test de notification service worker en cours." : "Testing service worker notification.",
      ),
    }));
    const result = await testServiceWorkerNotification();
    setBrowserPermission(getBrowserNotificationPermission());
    setPushDiagnostics((current) => ({
      ...current,
      busy: "",
      serviceWorkerNotification: result,
      message: result?.ok
        ? t(
            "settings.notificationPreferences.channels.push.swDiagnosticPass",
            isFrench ? "Notification service worker demandée." : "Service worker notification requested.",
          )
        : result?.detail || result?.reason || "Service worker notification failed.",
    }));
  }

  return (
    <SettingsScreenShell>
      <TopBarCenter
        close
        onBack={() => navigate(`${basePath}/preferences`)}
        title={t("notifications.common.title", isFrench ? "Notifications" : "Notifications")}
      />

      <div className="mt-6 space-y-3">
        <div className="px-0.5 text-body-small text-on-surface-var">
          {introCopy}
        </div>

        <Card className="overflow-hidden">
          <SettingsToggleRow
            title={rowTitle(
              titleCopy,
              CAPABILITIES.appointmentReminderScheduler ? null : "comingSoon",
              t
            )}
            support={timingCopy}
            trailing={
              <Switch
                checked={
                  CAPABILITIES.appointmentReminderScheduler &&
                  prefs.appointmentRemindersOn
                }
                disabled={!CAPABILITIES.appointmentReminderScheduler}
                onChange={(next) => {
                  if (!CAPABILITIES.appointmentReminderScheduler) return;
                  const on = toBool(next);
                  if (!on && prefs.appointmentRemindersOn) {
                    setConfirmReminderDeactivation(true);
                    return;
                  }
                  const nextPrefs = {
                    ...prefsRef.current,
                    appointmentRemindersOn: on,
                    emailOn: on ? prefsRef.current.emailOn : false,
                    smsOn: on ? prefsRef.current.smsOn : false,
                  };
                  void applyPreferenceIntent(
                    nextPrefs,
                    { appointment_reminders: buildAppointmentReminderPatch(nextPrefs, role) },
                    "notification_settings",
                  );
                }}
              />
            }
            divider
          />

          <SettingsToggleRow
            title={t("settings.notificationPreferences.channels.inApp.title", isFrench ? "Rappels dans l’application" : "In-app reminders")}
            support={t(
              "settings.notificationPreferences.channels.inApp.support",
              isFrench
                ? "Affiche les rappels de rendez-vous et les alertes de soins dans AfriMed™ lorsque vous êtes connecté."
                : "Shows appointment reminders and care alerts inside AfriMed™ while you are signed in.",
            )}
            trailing={
              <Switch
                checked={prefs.inAppOn}
                onChange={(next) => {
                  const on = toBool(next);
                  const nextPrefs = { ...prefsRef.current, inAppOn: on };
                  void applyPreferenceIntent(
                    nextPrefs,
                    {
                      appointment_reminders: buildAppointmentReminderPatch(nextPrefs, role),
                      device_alerts: buildDeviceAlertsPatch(nextPrefs, role),
                    },
                    "notification_settings",
                  );
                }}
              />
            }
            divider
          />

          <SettingsToggleRow
            title={rowTitle(
              t("settings.notificationPreferences.channels.sms.title", isFrench ? "Rappels par SMS" : "SMS reminders"),
              !CAPABILITIES.smsReminderDelivery ? "comingSoon" : null,
              t
            )}
            support={t(
              "settings.notificationPreferences.channels.sms.support",
              isFrench ? "Les rappels par SMS arrivent bientôt." : "SMS reminders are coming soon.",
            )}
            trailing={
              <Switch
                checked={
                  CAPABILITIES.appointmentReminderScheduler &&
                  CAPABILITIES.smsReminderDelivery &&
                  prefs.smsOn
                }
                disabled={
                  !CAPABILITIES.appointmentReminderScheduler ||
                  !CAPABILITIES.smsReminderDelivery
                }
                onChange={(next) => {
                  if (
                    !CAPABILITIES.appointmentReminderScheduler ||
                    !CAPABILITIES.smsReminderDelivery
                  ) return;
                  const on = toBool(next);
                  const nextPrefs = {
                    ...prefsRef.current,
                    smsOn: prefsRef.current.appointmentRemindersOn ? on : false,
                  };
                  void applyPreferenceIntent(
                    nextPrefs,
                    { appointment_reminders: buildAppointmentReminderPatch(nextPrefs, role) },
                    "notification_settings",
                  );
                }}
              />
            }
            divider
          />

          <SettingsToggleRow
            title={rowTitle(
              t("settings.notificationPreferences.channels.email.title", isFrench ? "Rappels par e-mail" : "Email reminders"),
              !CAPABILITIES.emailReminderDelivery ? "comingSoon" : null,
              t
            )}
            support={t(
              "settings.notificationPreferences.channels.email.support",
              isFrench ? "Envoie des e-mails de rappel lorsqu’un rappel est déclenché." : "Sends appointment reminder emails when a reminder is triggered.",
            )}
            trailing={
              <Switch
                checked={
                  CAPABILITIES.emailReminderDelivery &&
                  prefs.emailOn
                }
                disabled={!CAPABILITIES.emailReminderDelivery}
                onChange={(next) => {
                  if (!CAPABILITIES.emailReminderDelivery) return;
                  const on = toBool(next);
                  const nextPrefs = { ...prefsRef.current, emailOn: on };
                  void applyPreferenceIntent(
                    nextPrefs,
                    { appointment_reminders: buildAppointmentReminderPatch(nextPrefs, role) },
                    "notification_settings",
                  );
                }}
              />
            }
            divider
          />

          <SettingsToggleRow
            title={rowTitle(
              t("settings.notificationPreferences.channels.push.title", isFrench ? "Notifications push" : "Push notifications"),
              !CAPABILITIES.backgroundPushDelivery ? "comingSoon" : null,
              t
            )}
            support={
              !CAPABILITIES.backgroundPushDelivery
                ? t(
                    "settings.notificationPreferences.channels.push.supportForeground",
                    isFrench ? "Les notifications push arrivent bientôt." : "Push notifications are coming soon.",
                  )
                : !firebasePushConfigured
                  ? t(
                      "settings.notificationPreferences.channels.push.missingConfig",
                      isFrench
                        ? "Firebase Push n’est pas configuré pour cet environnement de staging. Les rappels dans AfriMed™ restent actifs."
                        : "Firebase push is not configured for this staging environment yet. In-app reminders remain active.",
                    )
                : browserPushDenied
                  ? t(
                      "settings.notificationPreferences.channels.push.denied",
                      isFrench
                        ? "Les notifications navigateur sont bloquées. Activez les notifications pour staging.afrimed.tech dans les paramètres du navigateur puis réessayez."
                        : "Browser notifications are blocked. Enable notifications for staging.afrimed.tech in your browser settings and try again.",
                    )
                : browserPushDefinitelyUnsupported
                  ? t(
                      "settings.notificationPreferences.channels.push.unsupported",
                      isFrench
                        ? "Les notifications push ne sont pas prises en charge par ce navigateur. Vous recevrez toujours les rappels dans AfriMed™ lorsque vous êtes connecté."
                        : "Push notifications are not supported on this browser. You will still receive in-app reminders while signed in.",
                    )
                : currentPushRegistered
                  ? t(
                      "settings.notificationPreferences.channels.push.registeredHint",
                      isFrench
                        ? "Ce navigateur est enregistré pour les notifications AfriMed™. Envoyez un test pour confirmer la livraison."
                        : "This browser is registered for AfriMed™ notifications. Send a test to confirm delivery.",
                    )
                : pushNeedsRefresh
                  ? t(
                      "settings.notificationPreferences.channels.push.refreshNeeded",
                      isFrench
                        ? "Les notifications push doivent être actualisées pour ce navigateur."
                        : "Push notifications need to be refreshed for this browser.",
                    )
                  : t(
                      "settings.notificationPreferences.channels.push.sessionInactive",
                      isFrench
                        ? "Les notifications push ne sont pas actives dans cette session de navigateur."
                        : "Push notifications are not active in this browser session.",
                    )
            }
            trailing={
              <Switch
                checked={currentPushRegistered}
                disabled={
                  !firebasePushConfigured ||
                  browserPushDefinitelyUnsupported ||
                  browserPushDenied ||
                  !CAPABILITIES.backgroundPushDelivery
                }
                onChange={handlePushToggle}
              />
            }
            divider
          />

          {CAPABILITIES.backgroundPushDelivery && !browserPushDefinitelyUnsupported ? (
            <div className="border-b border-[rgba(91,107,134,0.18)] px-4 py-3">
              {!currentPushRegistered ? (
                <Button
                  type="button"
                  variant="tonal"
                  size="default"
                  className="mb-2 mr-2 font-normal"
                  disabled={pushTestState.busy || !canAttemptPushRegistration}
                  onClick={() => handlePushToggle(true)}
                >
                  {pushNeedsRefresh
                    ? t(
                        "settings.notificationPreferences.channels.push.registerAgainButton",
                        isFrench ? "Réenregistrer ce navigateur" : "Register this browser again",
                      )
                    : t(
                        "settings.notificationPreferences.channels.push.registerButton",
                        isFrench ? "Enregistrer ce navigateur" : "Register this browser",
                      )}
                </Button>
              ) : null}
              <Button
                type="button"
                variant="tonal"
                size="default"
                className="font-normal"
                disabled={
                  pushTestState.busy ||
                  !firebasePushConfigured ||
                  browserPushDenied ||
                  !currentPushRegistered
                }
                onClick={handlePushTest}
              >
                {t(
                  "settings.notificationPreferences.channels.push.testButton",
                  isFrench ? "Envoyer un test push" : "Send test push",
                )}
              </Button>
              <p className="mt-2 text-body-small text-on-surface-var">
                {pushTestState.message ||
                  (!firebasePushConfigured
                    ? t(
                        "settings.notificationPreferences.channels.push.missingConfig",
                        isFrench
                          ? "Firebase Push n’est pas configuré pour cet environnement de staging. Les rappels dans AfriMed™ restent actifs."
                          : "Firebase push is not configured for this staging environment yet. In-app reminders remain active.",
                      )
                    : browserPushDenied
                    ? t(
                        "settings.notificationPreferences.channels.push.denied",
                        isFrench
                          ? "Les notifications navigateur sont bloquées. Activez les notifications pour staging.afrimed.tech dans les paramètres du navigateur puis réessayez."
                          : "Browser notifications are blocked. Enable notifications for staging.afrimed.tech in your browser settings and try again.",
                      )
                    : currentPushRegistered
                      ? t(
                          "settings.notificationPreferences.channels.push.registeredHint",
                          isFrench
                            ? "Ce navigateur est enregistré pour les notifications AfriMed™. Envoyez un test pour confirmer la livraison."
                            : "This browser is registered for AfriMed™ notifications. Send a test to confirm delivery.",
                        )
                    : pushNeedsRefresh
                      ? t(
                          "settings.notificationPreferences.channels.push.refreshNeeded",
                          isFrench
                            ? "Les notifications push doivent être actualisées pour ce navigateur."
                            : "Push notifications need to be refreshed for this browser.",
                        )
                      : t(
                          "settings.notificationPreferences.channels.push.sessionInactive",
                          isFrench
                            ? "Les notifications push ne sont pas actives dans cette session de navigateur."
                          : "Push notifications are not active in this browser session.",
                        ))}
              </p>
              {pushVisibleProof ? (
                <div
                  className={[
                    "mt-3 rounded-[8px] border px-3 py-2 text-body-small",
                    pushVisibleProof.tone === "success"
                      ? "border-[rgba(22,163,74,0.28)] bg-[rgba(22,163,74,0.08)] text-[rgb(21,128,61)]"
                      : pushVisibleProof.tone === "warning"
                        ? "border-[rgba(217,119,6,0.28)] bg-[rgba(217,119,6,0.08)] text-[rgb(146,64,14)]"
                        : "border-[rgba(24,50,119,0.18)] bg-[rgba(24,50,119,0.06)] text-on-surface-var",
                  ].join(" ")}
                >
                  <div className="font-semibold text-on-surface">
                    {pushVisibleProof.title}
                  </div>
                  <div className="mt-1">{pushVisibleProof.body}</div>
                </div>
              ) : null}
              {showPushDiagnostics ? (
                <div className="mt-3 rounded-[10px] border border-[rgba(24,50,119,0.18)] bg-[rgba(24,50,119,0.04)] p-3 text-body-small text-on-surface-var">
                  <div className="font-semibold text-on-surface">
                    {t(
                      "settings.notificationPreferences.channels.push.desktopDiagnosticsTitle",
                      isFrench ? "Diagnostic desktop push (staging)" : "Desktop push diagnostics (staging)",
                    )}
                  </div>
                  <div className="mt-1">
                    {t(
                      "settings.notificationPreferences.channels.push.desktopDiagnosticsSupport",
                      isFrench
                        ? "Visible uniquement avec debugPush=1. Ces tests séparent navigateur, service worker et Firebase."
                        : "Visible only with debugPush=1. These tests separate browser, service worker and Firebase delivery.",
                    )}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="tonal"
                      size="sm"
                      className="font-normal"
                      disabled={Boolean(pushDiagnostics.busy)}
                      onClick={refreshPushDiagnostics}
                    >
                      {t(
                        "settings.notificationPreferences.channels.push.refreshDiagnostics",
                        isFrench ? "Actualiser" : "Refresh diagnostics",
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="tonal"
                      size="sm"
                      className="font-normal"
                      disabled={Boolean(pushDiagnostics.busy)}
                      onClick={handleDirectBrowserNotificationDiagnostic}
                    >
                      {t(
                        "settings.notificationPreferences.channels.push.testBrowserNotification",
                        isFrench ? "Tester notification navigateur" : "Test browser notification",
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="tonal"
                      size="sm"
                      className="font-normal"
                      disabled={Boolean(pushDiagnostics.busy)}
                      onClick={handleServiceWorkerNotificationDiagnostic}
                    >
                      {t(
                        "settings.notificationPreferences.channels.push.testServiceWorkerNotification",
                        isFrench ? "Tester service worker" : "Test service worker notification",
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="tonal"
                      size="sm"
                      className="font-normal"
                      disabled={Boolean(pushDiagnostics.busy) || !prefs.browserPushOn}
                      onClick={() => handleFirebaseBackgroundDiagnostic("notification_plus_data")}
                    >
                      {t(
                        "settings.notificationPreferences.channels.push.testFirebaseBackground",
                        isFrench ? "Tester Firebase arrière-plan" : "Test Firebase background",
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="tonal"
                      size="sm"
                      className="font-normal"
                      disabled={Boolean(pushDiagnostics.busy) || !prefs.browserPushOn}
                      onClick={() => handleFirebaseBackgroundDiagnostic("notification_only")}
                    >
                      {isFrench ? "Notification seule" : "Notification-only"}
                    </Button>
                    <Button
                      type="button"
                      variant="tonal"
                      size="sm"
                      className="font-normal"
                      disabled={Boolean(pushDiagnostics.busy) || !prefs.browserPushOn}
                      onClick={() => handleFirebaseBackgroundDiagnostic("data_only")}
                    >
                      {isFrench ? "Données seules" : "Data-only"}
                    </Button>
                    <Button
                      type="button"
                      variant="tonal"
                      size="sm"
                      className="font-normal"
                      disabled={Boolean(pushDiagnostics.busy) || !prefs.browserPushOn}
                      onClick={() => handleFirebaseBackgroundDiagnostic("notification_plus_data", 10)}
                    >
                      {isFrench ? "Test différé 10s" : "Delayed background test (10s)"}
                    </Button>
                  </div>
                  <div className="mt-2">
                    {isFrench
                      ? "Pour le test différé : cliquez, puis quittez AfriMed™ avant l’envoi."
                      : "For the delayed test: click, then switch away from AfriMed™ before the test sends."}
                  </div>
                  {pushDiagnostics.message ? (
                    <div className="mt-2">{pushDiagnostics.message}</div>
                  ) : null}
                  <dl className="mt-3 grid gap-2 sm:grid-cols-2">
                    <div>
                      <dt className="font-semibold text-on-surface">Config</dt>
                      <dd>
                        {pushDiagnostics.runtime?.configured ? "configured" : "not configured"}
                        {pushDiagnostics.runtime?.missing?.length
                          ? ` (${pushDiagnostics.runtime.missing.join(", ")})`
                          : ""}
                      </dd>
                    </div>
                    <div>
                      <dt className="font-semibold text-on-surface">Permission</dt>
                      <dd>{pushDiagnostics.runtime?.notificationPermission || browserPermission}</dd>
                    </div>
                    <div>
                      <dt className="font-semibold text-on-surface">Service worker</dt>
                      <dd>
                        {pushDiagnostics.runtime?.serviceWorkerReady ? "ready" : "not ready"}
                        {pushDiagnostics.runtime?.serviceWorkerScope
                          ? ` (${pushDiagnostics.runtime.serviceWorkerScope})`
                          : ""}
                      </dd>
                    </div>
                    <div>
                      <dt className="font-semibold text-on-surface">Browser test</dt>
                      <dd>
                        {pushDiagnostics.browserNotification
                          ? pushDiagnostics.browserNotification.ok
                            ? "requested"
                            : pushDiagnostics.browserNotification.reason || "failed"
                          : "not run"}
                      </dd>
                    </div>
                    <div>
                      <dt className="font-semibold text-on-surface">Service worker test</dt>
                      <dd>
                        {pushDiagnostics.serviceWorkerNotification
                          ? pushDiagnostics.serviceWorkerNotification.ok
                            ? "requested"
                            : pushDiagnostics.serviceWorkerNotification.reason || "failed"
                          : "not run"}
                      </dd>
                    </div>
                    <div>
                      <dt className="font-semibold text-on-surface">Firebase current-token send</dt>
                      <dd>
                        {pushDiagnostics.firebaseSend
                          ? pushDiagnostics.firebaseSend.pushSent
                            ? `accepted (${maskIdentifier(pushDiagnostics.firebaseSend.firebaseResponseName || "")})`
                            : pushDiagnostics.firebaseSend.reason || "not accepted"
                          : "not run"}
                      </dd>
                    </div>
                    <div>
                      <dt className="font-semibold text-on-surface">Target device</dt>
                      <dd>
                        {pushDiagnostics.firebaseSend?.deviceId
                          ? maskIdentifier(pushDiagnostics.firebaseSend.deviceId)
                          : pushDiagnostics.firebaseBackgroundSend?.deviceId
                            ? maskIdentifier(pushDiagnostics.firebaseBackgroundSend.deviceId)
                            : "not known"}
                      </dd>
                    </div>
                    <div>
                      <dt className="font-semibold text-on-surface">Payload type</dt>
                      <dd>
                        {pushDiagnostics.firebaseSend?.payloadType ||
                          pushDiagnostics.firebaseBackgroundSend?.payloadType ||
                          "not run"}
                      </dd>
                    </div>
                    <div>
                      <dt className="font-semibold text-on-surface">Payload modes tested</dt>
                      <dd>
                        {["notification_only", "data_only", "notification_plus_data"]
                          .map((mode) => {
                            const item = pushDiagnostics.payloadSends?.[mode];
                            const label = mode
                              .replace("notification_only", "notification")
                              .replace("data_only", "data")
                              .replace("notification_plus_data", "mixed");
                            return `${label}: ${
                              item
                                ? item.pushSent
                                  ? "accepted"
                                  : item.reason || "failed"
                                : "not run"
                            }`;
                          })
                          .join(" · ")}
                      </dd>
                    </div>
                    <div>
                      <dt className="font-semibold text-on-surface">Firebase background send</dt>
                      <dd>
                        {pushDiagnostics.firebaseBackgroundSend
                          ? pushDiagnostics.firebaseBackgroundSend.pushSent
                            ? `accepted (${maskIdentifier(pushDiagnostics.firebaseBackgroundSend.firebaseResponseName || "")})`
                            : pushDiagnostics.firebaseBackgroundSend.reason || "not accepted"
                          : "not run"}
                      </dd>
                    </div>
                    <div>
                      <dt className="font-semibold text-on-surface">Foreground handler</dt>
                      <dd>
                        {(pushDiagnostics.pushEvents || []).find((item) =>
                          String(item.phase || "").startsWith("foreground_")
                        )?.phase || "not observed"}
                      </dd>
                    </div>
                    <div>
                      <dt className="font-semibold text-on-surface">SW push event</dt>
                      <dd>
                        {hasPushEvent(pushDiagnostics.pushEvents || [], "sw_push_event_received")
                          ? "received"
                          : "not observed"}
                      </dd>
                    </div>
                    <div>
                      <dt className="font-semibold text-on-surface">Background handler</dt>
                      <dd>
                        {(pushDiagnostics.pushEvents || []).find((item) =>
                          String(item.phase || "").startsWith("background_") ||
                          item.phase === "firebase_background_message_received"
                        )?.phase || "not observed"}
                      </dd>
                    </div>
                    <div>
                      <dt className="font-semibold text-on-surface">showNotification from Firebase path</dt>
                      <dd>
                        {findPushEvent(pushDiagnostics.pushEvents || [], "background_show_notification_success")
                          ? "success"
                          : findPushEvent(pushDiagnostics.pushEvents || [], "background_show_notification_error")
                            ? `error: ${findPushEvent(pushDiagnostics.pushEvents || [], "background_show_notification_error")?.detail || "unknown"}`
                            : hasPushEvent(pushDiagnostics.pushEvents || [], "background_show_notification_attempted")
                              ? "attempted"
                              : "not observed"}
                      </dd>
                    </div>
                    <div>
                      <dt className="font-semibold text-on-surface">Raw push fallback showNotification</dt>
                      <dd>
                        {findPushEvent(pushDiagnostics.pushEvents || [], "sw_show_notification_success")
                          ? "success"
                          : findPushEvent(pushDiagnostics.pushEvents || [], "sw_show_notification_error")
                            ? `error: ${findPushEvent(pushDiagnostics.pushEvents || [], "sw_show_notification_error")?.detail || "unknown"}`
                            : hasPushEvent(pushDiagnostics.pushEvents || [], "sw_show_notification_attempted")
                              ? "attempted"
                              : findPushEvent(pushDiagnostics.pushEvents || [], "sw_show_notification_skipped")
                                ? `skipped: ${findPushEvent(pushDiagnostics.pushEvents || [], "sw_show_notification_skipped")?.detail || findPushEvent(pushDiagnostics.pushEvents || [], "sw_show_notification_skipped")?.reason || "see events"}`
                                : "not observed"}
                      </dd>
                    </div>
                  </dl>
                  {pushDiagnostics.pushEvents?.length ? (
                    <div className="mt-3 rounded-[8px] bg-[rgba(255,255,255,0.58)] p-2">
                      <div className="font-semibold text-on-surface">Recent push events</div>
                      <ul className="mt-1 space-y-1">
                        {pushDiagnostics.pushEvents.map((event, index) => (
                          <li key={`${event.at || "event"}-${index}`}>
                            {event.phase}
                            {event.type ? ` · ${event.type}` : ""}
                            {event.payloadMode ? ` · ${event.payloadMode}` : ""}
                            {event.detail || event.reason ? ` · ${event.detail || event.reason}` : ""}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}

          <SettingsToggleRow
            title={t("settings.notificationPreferences.channels.sound.title", isFrench ? "Son d’alerte urgente" : "Urgent alert sound")}
            support={t(
              "settings.notificationPreferences.channels.sound.support",
              isFrench
                ? "Joue un son bref pour les alertes de soins urgentes lorsque AfriMed™ est ouvert."
                : "Plays a short tone for urgent care alerts while AfriMed™ is open.",
            )}
            trailing={
              <Switch
                checked={prefs.soundOn}
                onChange={(next) => {
                  const on = toBool(next);
                  const nextPrefs = {
                    ...prefsRef.current,
                    soundOn: on,
                    messageAlertSound: on
                      ? prefsRef.current.messageAlertSound !== "off"
                        ? prefsRef.current.messageAlertSound
                        : DEFAULT_MESSAGE_ALERT_SOUND
                      : "off",
                  };
                  void applyPreferenceIntent(
                    nextPrefs,
                    { device_alerts: buildDeviceAlertsPatch(nextPrefs, role) },
                    "notification_settings",
                  );
                }}
              />
            }
            divider
          />

          <SettingsToggleRow
            title={rowTitle(
              t("settings.notificationPreferences.channels.vibration.title", isFrench ? "Vibration urgente" : "Urgent vibration"),
              !vibrationSupported,
              t
            )}
            support={t(
              "settings.notificationPreferences.channels.vibration.support",
              vibrationSupported
                ? isFrench
                  ? "Ajoute une brève vibration sur les appareils compatibles pour les alertes de soins urgentes."
                  : "Adds a short haptic pulse on supported devices for urgent care alerts."
                : isFrench
                  ? "La vibration urgente utilise les retours haptiques compatibles. Bientôt disponible sur les appareils non compatibles."
                  : "Urgent vibration uses supported device haptics. Coming soon on unsupported devices.",
            )}
            trailing={
              <Switch
                checked={vibrationSupported && prefs.vibrationOn}
                disabled={!vibrationSupported}
                onChange={(next) => {
                  if (!vibrationSupported) return;
                  const on = toBool(next);
                  const nextPrefs = { ...prefsRef.current, vibrationOn: on };
                  void applyPreferenceIntent(
                    nextPrefs,
                    { device_alerts: buildDeviceAlertsPatch(nextPrefs, role) },
                    "notification_settings",
                  );
                }}
              />
            }
            divider={false}
          />
        </Card>

      </div>
      {confirmReminderDeactivation ? (
        <BasicDialogCritical
          open
          title={t(
            "settings.notificationPreferences.reminders.deactivate.title",
            "Are you sure you want to switch off reminders for this appointment?",
          )}
          body={t(
            "settings.notificationPreferences.reminders.deactivate.copy",
            "If you turn off reminders for this appointment, you may miss the appointment.",
          )}
          iconSrc="/icons/clinicians/report.svg"
          cancelText={t("settings.notificationPreferences.reminders.deactivate.keep", "Keep reminders on")}
          okText={t("settings.notificationPreferences.reminders.deactivate.confirm", "Switch off reminders")}
          onCancel={() => setConfirmReminderDeactivation(false)}
          onOk={() => {
            const nextPrefs = {
              ...prefsRef.current,
              appointmentRemindersOn: false,
              emailOn: false,
              smsOn: false,
              browserPushOn: false,
            };
            void applyPreferenceIntent(
              nextPrefs,
              { appointment_reminders: buildAppointmentReminderPatch(nextPrefs, role) },
              "notification_settings",
            );
            setConfirmReminderDeactivation(false);
          }}
        />
      ) : null}
    </SettingsScreenShell>
  );
}
